import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Layout,
    Typography,
    Space,
    Button,
    Spin,
    Select,
    Alert,
    message,
    Modal,
    theme,
    Grid,
} from 'antd';
import {
    PlusOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { salesPlanningApi } from '../../api/client';
import WorkspacePlan from './WorkspacePlan';
import NewPlanWizard from './components/NewPlanWizard';
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// Basic Error Boundary
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error: Error | null }> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: any) {
        console.error("SalesPlanningPageV3 Crash:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <Alert
                    type="error"
                    message="Błąd krytyczny komponentu"
                    description={
                        <div>
                            <p>Wystąpił błąd podczas renderowania strony planowania.</p>
                            <pre style={{ maxWidth: 800, overflow: 'auto' }}>{this.state.error?.toString()}</pre>
                        </div>
                    }
                    showIcon
                    style={{ margin: 20 }}
                />
            );
        }
        return this.props.children;
    }
}

function Badge(props: any) {
    return <span style={{ width: 8, height: 8, borderRadius: '50%', background: props.status === 'ACTIVE' ? '#52c41a' : '#ccc', display: 'inline-block', marginRight: 8 }} />
}

export default function SalesPlanningPageV3() {
    const queryClient = useQueryClient();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { token } = theme.useToken();
    const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);

    // Fetch Plans List
    const { data: plansData, isLoading: plansLoading } = useQuery({
        queryKey: ['sales-plans'],
        queryFn: () => salesPlanningApi.getAll().then(res => res.data),
    });

    const plans = plansData || [];

    // Select latest plan by default
    useEffect(() => {
        if (!selectedPlanId && plans.length > 0) {
            setSelectedPlanId(plans[0].id);
        }
    }, [plans]);

    // Plan Creation Mutation
    const createPlanMutation = useMutation({
        mutationFn: (values: any) => salesPlanningApi.create(values),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['sales-plans'] });
            message.success('Nowy plan został utworzony');
            setIsCreateModalVisible(false);
            if (res.data?.id) {
                setSelectedPlanId(res.data.id);
            }
        },
        onError: (err: any) => {
            message.error(`Błąd podczas tworzenia planu: ${err.response?.data?.message || err.message}`);
        }
    });

    const handleCreatePlan = (values: any) => {
        createPlanMutation.mutate(values);
    };

    if (plansLoading) {
        return <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>;
    }

    return (
        <ErrorBoundary>
            <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
                <Content style={{ padding: isMobile ? '12px' : '24px', maxWidth: 1800, margin: '0 auto', width: '100%' }}>
                    {/* Header */}
                    <div style={{ 
                        marginBottom: 16, 
                        display: 'flex', 
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between', 
                        alignItems: isMobile ? 'flex-start' : 'center',
                        gap: 16
                    }}>
                        <Space size="large" direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }}>
                            <div>
                                <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>Planowanie Sprzedaży</Title>
                                <Text type="secondary">System Prognozowania i Budżetowania</Text>
                            </div>

                            <Space direction="vertical" size={0} style={{ width: isMobile ? '100%' : 'auto' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Aktywny Plan</Text>
                                <Select
                                    style={{ width: isMobile ? '100%' : 320 }}
                                    placeholder="Wybierz plan"
                                    value={selectedPlanId}
                                    onChange={setSelectedPlanId}
                                    options={plans.map((p: any) => ({
                                        value: p.id,
                                        label: (
                                            <Space>
                                                <Badge status={p.status} />
                                                {p.name}
                                                <span style={{ color: '#aaa' }}>{dayjs(p.periodStart).format('YY')}</span>
                                            </Space>
                                        )
                                    }))}
                                />
                            </Space>

                            <Button icon={<PlusOutlined />} onClick={() => setIsCreateModalVisible(true)} block={isMobile}>Nowy Plan</Button>
                        </Space>


                    </div>

                    {/* Workspace Area */}
                    {selectedPlanId ? (
                        <WorkspacePlan planId={selectedPlanId} />
                    ) : (
                        <div style={{ padding: isMobile ? 40 : 100, textAlign: 'center', background: token.colorBgContainer, borderRadius: 8 }}>
                            <Title level={4}>Wybierz plan z listy aby rozpocząć pracę</Title>
                        </div>
                    )}

                    <NewPlanWizard
                        visible={isCreateModalVisible}
                        onCancel={() => setIsCreateModalVisible(false)}
                        onCreate={handleCreatePlan}
                        isLoading={createPlanMutation.isPending}
                    />
                </Content>
            </Layout>
        </ErrorBoundary>
    );
}
